<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  pipelines: { type: Array, default: () => [] },
  modelValue: { type: Number, default: null },
  activePipelineId: { type: Number, default: null },
  placeholder: { type: String, default: 'Selecionar etapa' },
  hasError: { type: Boolean, default: false },
  message: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const isOpen = ref(false);
const openPipelineId = ref(null);
const triggerRef = ref(null);
const dropdownRef = ref(null);

const selectedStage = computed(() => {
  const pipeline = props.pipelines.find(p =>
    (p.stages || []).some(s => s.id === props.modelValue)
  );
  if (!pipeline) return null;
  const stage = pipeline.stages.find(s => s.id === props.modelValue);
  return { pipeline, stage };
});

watch(
  () => props.activePipelineId,
  id => {
    if (id) openPipelineId.value = id;
  },
  { immediate: true }
);

watch(isOpen, open => {
  if (open && !openPipelineId.value) {
    openPipelineId.value =
      props.activePipelineId ?? props.pipelines[0]?.id ?? null;
  }
});

const toggle = pipelineId => {
  openPipelineId.value =
    openPipelineId.value === pipelineId ? null : pipelineId;
};

const select = stageId => {
  emit('update:modelValue', stageId);
  isOpen.value = false;
};

const handleClickOutside = e => {
  if (
    triggerRef.value &&
    !triggerRef.value.contains(e.target) &&
    dropdownRef.value &&
    !dropdownRef.value.contains(e.target)
  ) {
    isOpen.value = false;
  }
};

onMounted(() => document.addEventListener('mousedown', handleClickOutside));
onBeforeUnmount(() =>
  document.removeEventListener('mousedown', handleClickOutside)
);
</script>

<template>
  <div class="relative">
    <button
      ref="triggerRef"
      type="button"
      class="flex items-center justify-between w-full h-8 px-3 rounded-lg border text-sm transition-colors bg-n-alpha-black2"
      :class="{
        'border-n-brand ring-1 ring-n-brand text-n-slate-12': isOpen,
        'border-transparent text-n-slate-11 hover:border-n-brand':
          !isOpen && !hasError,
        'border-n-ruby-8 ring-1 ring-n-ruby-8 text-n-slate-11':
          !isOpen && hasError,
      }"
      @click="isOpen = !isOpen"
    >
      <span v-if="selectedStage" class="text-n-slate-12 truncate">
        {{ selectedStage.stage.name }}
        <span class="text-n-slate-10 text-xs ml-1">
          · {{ selectedStage.pipeline.name }}
        </span>
      </span>
      <span v-else class="text-n-slate-10">{{ placeholder }}</span>
      <Icon
        :icon="isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        class="size-4 flex-shrink-0 ml-2"
        :class="isOpen ? 'text-n-brand' : 'text-n-slate-11'"
      />
    </button>
    <p
      v-if="message"
      class="mt-2 mb-0 text-xs truncate transition-all duration-500 ease-in-out"
      :class="{
        'text-n-ruby-9': hasError,
        'text-n-slate-11': !hasError,
      }"
    >
      {{ message }}
    </p>

    <div
      v-if="isOpen"
      ref="dropdownRef"
      class="absolute z-50 left-0 right-0 mt-1 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg overflow-hidden max-h-72 overflow-y-auto"
    >
      <div
        v-for="pipeline in pipelines"
        :key="pipeline.id"
        class="border-b border-n-weak last:border-b-0"
      >
        <button
          type="button"
          class="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10 hover:bg-n-alpha-1 transition-colors"
          @click="toggle(pipeline.id)"
        >
          <span>{{ pipeline.name }}</span>
          <Icon
            :icon="
              openPipelineId === pipeline.id
                ? 'i-lucide-chevron-up'
                : 'i-lucide-chevron-down'
            "
            class="size-3.5 flex-shrink-0"
          />
        </button>

        <div v-if="openPipelineId === pipeline.id">
          <button
            v-for="stage in pipeline.stages || []"
            :key="stage.id"
            type="button"
            class="flex items-center gap-2 w-full px-4 py-2 text-sm text-left transition-colors"
            :class="
              modelValue === stage.id
                ? 'text-n-brand bg-n-alpha-1 font-medium'
                : 'text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="select(stage.id)"
          >
            <Icon
              v-if="modelValue === stage.id"
              icon="i-lucide-check"
              class="size-3.5 text-n-brand flex-shrink-0"
            />
            <span :class="modelValue !== stage.id ? 'ml-5' : ''">
              {{ stage.name }}
            </span>
          </button>
          <div
            v-if="!pipeline.stages || pipeline.stages.length === 0"
            class="px-4 py-2 text-xs text-n-slate-10 italic"
          >
            {{ t('DEALS.PIPELINE.EMPTY_STAGES') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
